// Global error handler for capturing unhandled errors
import { trackEvent, captureException } from '@/lib/posthog';
import { errorEvents } from './events/errors';

let isInitialized = false;

/**
 * Initialize global error handlers
 * Should be called once in the root layout
 */
export function initGlobalErrorHandler() {
  if (isInitialized) return;
  if (typeof window === 'undefined') return;

  isInitialized = true;

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const errorMessage = reason?.message || String(reason);
    const errorStack = reason?.stack;

    // Capture for PostHog Error Tracking dashboard ($exception event)
    if (reason instanceof Error) {
      captureException(reason, { source: 'unhandled_promise_rejection' });
    } else {
      // Create an Error object for non-Error rejections
      const error = new Error(errorMessage);
      error.stack = errorStack;
      captureException(error, { source: 'unhandled_promise_rejection' });
    }

    // Also track as custom event for backwards compatibility
    // Safely handle errorStack - it might not be a string
    const stackTrace = typeof errorStack === 'string' ? errorStack.substring(0, 500) : undefined;
    const { event: eventName, properties } = errorEvents.promiseRejection(
      errorMessage,
      stackTrace
    );
    trackEvent(eventName, properties);

    // Log in development
    if (import.meta.env.DEV) {
      console.error('[ErrorHandler] Unhandled promise rejection:', reason);
    }
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    // Capture for PostHog Error Tracking dashboard ($exception event)
    if (event.error instanceof Error) {
      captureException(event.error, {
        source: 'global_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    } else {
      // Create an Error object for non-Error events
      const error = new Error(event.message);
      captureException(error, {
        source: 'global_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    }

    // Also track as custom event for backwards compatibility
    const { event: eventName, properties } = errorEvents.unhandledError(
      event.message,
      event.error?.stack,
      `${event.filename}:${event.lineno}:${event.colno}`
    );
    trackEvent(eventName, properties);

    // Log in development
    if (import.meta.env.DEV) {
      console.error('[ErrorHandler] Global error:', event.error);
    }
  });

  if (import.meta.env.DEV) {
    console.log('[ErrorHandler] Global error handlers initialized');
  }
}

/**
 * Wrap an async function to automatically track errors
 */
export function withErrorTracking<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  context: { feature: string; operation: string }
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const { event: eventName, properties } = errorEvents.featureError(
        context.feature,
        context.operation,
        errorMessage
      );
      trackEvent(eventName, properties);
      throw error;
    }
  }) as T;
}

/**
 * Track an API error
 */
export function trackApiError(
  endpoint: string,
  statusCode: number,
  errorMessage: string,
  context?: Record<string, unknown>
) {
  const { event: eventName, properties } = errorEvents.apiError(
    endpoint,
    statusCode,
    errorMessage,
    context
  );
  trackEvent(eventName, properties);
}

/**
 * Track a Supabase error
 */
export function trackSupabaseError(
  operation: string,
  table: string,
  error: { message: string; code?: string }
) {
  const { event: eventName, properties } = errorEvents.supabaseError(
    operation,
    table,
    error.message,
    error.code
  );
  trackEvent(eventName, properties);
}

/**
 * Track an edge function error
 */
export function trackEdgeFunctionError(
  functionName: string,
  errorMessage: string,
  durationMs?: number
) {
  const { event: eventName, properties } = errorEvents.edgeFunctionError(
    functionName,
    errorMessage,
    durationMs
  );
  trackEvent(eventName, properties);
}

/**
 * Track a Stripe error
 */
export function trackStripeError(
  action: string,
  error: { message: string; code?: string; decline_code?: string }
) {
  const { event: eventName, properties } = errorEvents.stripeError(
    action,
    error.message,
    error.code,
    error.decline_code
  );
  trackEvent(eventName, properties);
}

/**
 * Track a validation error
 */
export function trackValidationError(
  form: string,
  field: string,
  errorType: string,
  errorMessage?: string
) {
  const { event: eventName, properties } = errorEvents.validationError(
    form,
    field,
    errorType,
    errorMessage
  );
  trackEvent(eventName, properties);
}

export default {
  init: initGlobalErrorHandler,
  trackApiError,
  trackSupabaseError,
  trackEdgeFunctionError,
  trackStripeError,
  trackValidationError,
  withErrorTracking,
};
